import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Button, Input, Select, Tabs, Space, message, Spin, Modal, Table,
  Tag, Tooltip, Empty
} from 'antd';
import {
  ArrowLeftOutlined, SaveOutlined, SendOutlined, EyeOutlined,
  PlusOutlined,
  TeamOutlined, FormOutlined, ExperimentOutlined,
  CheckCircleFilled
} from '@ant-design/icons';
import EmailBodyEditor from './EmailBodyEditor';
import EmailCampaignService from '../../../services/EmailCampaignService';
import EmailTemplateService from '../../../services/EmailTemplateService';
import SurveyService from '../../../services/SurveyService';
import ROUTES from '../../../constants/routes';
import { BuilderContainer, TopBar, ContentArea, BrandingCard, TabsContainer } from '../../Surveys/Builder/styled';

const { TextArea } = Input;

const defaultCampaign = {
  name: '',
  description: '',
  subject: '',
  html: '<p>Hello {{firstName}},</p>\n<p>We\'d love your feedback. Please take a moment to complete our survey:</p>\n<p><a href="{{surveyUrl}}">Take the Survey</a></p>\n<p>Thank you,<br/>The Black Heart Association Team</p>',
  text: '',
  fromName: 'Black Heart Association',
  fromEmail: 'info@blackheartassociation.org',
  replyTo: '',
  templateId: null,
  linkedSurveyId: null,
  defaultVariables: {}
};

const CampaignBuilder = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = !id;

  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('content');
  const [campaign, setCampaign] = useState(defaultCampaign);
  const [templates, setTemplates] = useState([]);
  const [surveys, setSurveys] = useState([]);
  const [verifiedSenders, setVerifiedSenders] = useState([]);
  const [recipients, setRecipients] = useState([]);
  const [recipientPagination, setRecipientPagination] = useState({ current: 1, pageSize: 50, total: 0 });
  const [addRecipientsModalOpen, setAddRecipientsModalOpen] = useState(false);
  const [recipientsBulkText, setRecipientsBulkText] = useState('');
  const [testEmailModalOpen, setTestEmailModalOpen] = useState(false);
  const [testEmail, setTestEmail] = useState('');

  const loadCampaign = useCallback(async () => {
    if (isNew) return;
    const result = await EmailCampaignService.getCampaign(id);
    if (result.success) {
      setCampaign(result.data);
    } else {
      message.error(result.error);
    }
  }, [id, isNew]);

  const loadRecipients = useCallback(async () => {
    if (isNew) return;
    const result = await EmailCampaignService.getRecipients(id, {
      page: recipientPagination.current,
      limit: recipientPagination.pageSize
    });
    if (result.success) {
      setRecipients(result.data.recipients || []);
      setRecipientPagination((p) => ({ ...p, total: result.data.pagination?.total || 0 }));
    }
  }, [id, isNew, recipientPagination.current, recipientPagination.pageSize]);

  useEffect(() => {
    (async () => {
      const [tplResult, survResult, sendersResult] = await Promise.all([
        EmailTemplateService.listTemplates({ limit: 100 }),
        SurveyService.listSurveys({ limit: 100, status: 'active' }),
        EmailCampaignService.getVerifiedSenders()
      ]);
      if (tplResult.success) setTemplates(tplResult.data.templates || []);
      if (survResult.success) setSurveys(survResult.data.surveys || []);
      if (sendersResult.success) setVerifiedSenders(sendersResult.data.senders || []);
      await loadCampaign();
      setLoading(false);
    })();
  }, [loadCampaign]);

  // Default From Email to info@blackheartassociation.org for any campaign
  // that doesn't already have one (covers existing drafts saved before a
  // default was applied). Only runs after the senders list has loaded and
  // only if info is actually verified.
  useEffect(() => {
    if (!verifiedSenders.length) return;
    if (campaign.fromEmail) return;
    const preferred = verifiedSenders.find(
      (s) => s.email === 'info@blackheartassociation.org'
    );
    if (preferred) {
      setCampaign((c) => ({
        ...c,
        fromEmail: preferred.email,
        fromName: c.fromName || preferred.name
      }));
    }
  }, [verifiedSenders, campaign.fromEmail]);

  useEffect(() => { loadRecipients(); }, [loadRecipients]);

  const updateCampaign = (patch) => setCampaign((c) => ({ ...c, ...patch }));

  const applyTemplate = (templateId) => {
    if (!templateId) {
      updateCampaign({ templateId: null });
      return;
    }
    const tpl = templates.find((t) => t._id === templateId);
    if (tpl) {
      updateCampaign({
        templateId,
        subject: campaign.subject || tpl.subject,
        html: tpl.html,
        text: tpl.text || ''
      });
      message.info('Template applied — content updated');
    }
  };

  const handleSave = async () => {
    if (!campaign.name) {
      message.warning('Please enter a campaign name');
      return;
    }
    setSaving(true);
    const payload = { ...campaign };
    let result;
    if (isNew) {
      result = await EmailCampaignService.createCampaign(payload);
      if (result.success) {
        message.success('Campaign created');
        navigate(`/admin/campaigns/${result.data._id}/edit`, { replace: true });
      }
    } else {
      result = await EmailCampaignService.updateCampaign(id, payload);
      if (result.success) {
        message.success('Saved');
        setCampaign((c) => ({ ...c, ...result.data }));
      }
    }
    if (!result.success) message.error(result.error);
    setSaving(false);
  };

  const handleAddRecipients = async () => {
    const lines = recipientsBulkText
      .split(/\r?\n/)
      .map((l) => l.trim())
      .filter((l) => l);

    const parsed = lines.map((line) => {
      // Format: email | email,firstName,lastName
      const parts = line.split(',').map((s) => s.trim());
      return {
        email: parts[0],
        firstName: parts[1] || '',
        lastName: parts[2] || ''
      };
    });

    if (!parsed.length) {
      message.warning('Please enter at least one email');
      return;
    }

    const result = await EmailCampaignService.addRecipients(id, parsed);
    if (result.success) {
      message.success(`Added ${result.data.added} recipients (${result.data.skipped} skipped)`);
      setRecipientsBulkText('');
      setAddRecipientsModalOpen(false);
      loadRecipients();
      loadCampaign();
    } else {
      message.error(result.error);
    }
  };

  const handleSendTest = async () => {
    if (!testEmail) {
      message.warning('Enter a test email');
      return;
    }
    const result = await EmailCampaignService.sendTestEmail(id, testEmail);
    if (result.success) {
      message.success(`Test email sent to ${testEmail}`);
      setTestEmailModalOpen(false);
      setTestEmail('');
    } else {
      message.error(result.error);
    }
  };

  const handleSendCampaign = () => {
    Modal.confirm({
      title: 'Send this campaign?',
      content: `This will send the email to all ${campaign.stats?.totalRecipients || 0} pending recipients. This cannot be undone.`,
      okText: 'Send Now',
      okType: 'danger',
      onOk: async () => {
        const result = await EmailCampaignService.sendCampaign(id);
        if (result.success) {
          message.success('Campaign sending started');
          loadCampaign();
          setTimeout(() => navigate(`/admin/campaigns/${id}/analytics`), 1500);
        } else {
          message.error(result.error);
        }
      }
    });
  };

  if (loading) {
    return (
      <BuilderContainer>
        <div style={{ padding: 60, textAlign: 'center' }}><Spin size="large" /></div>
      </BuilderContainer>
    );
  }

  const isLocked = ['sending', 'sent'].includes(campaign.status);

  return (
    <BuilderContainer>
      <TopBar>
        <Space>
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(ROUTES.ADMIN_EMAIL_CAMPAIGNS)}>Back</Button>
          <Input
            className="title-input"
            value={campaign.name}
            onChange={(e) => updateCampaign({ name: e.target.value })}
            placeholder="Campaign Name"
            disabled={isLocked}
          />
          <Tag color={
            campaign.status === 'sent' ? 'green'
              : campaign.status === 'sending' ? 'processing'
              : campaign.status === 'failed' ? 'red' : 'default'
          }>
            {campaign.status?.toUpperCase() || 'DRAFT'}
          </Tag>
        </Space>
        <Space>
          {!isNew && !isLocked && (
            <Button icon={<ExperimentOutlined />} onClick={() => setTestEmailModalOpen(true)}>
              Send Test
            </Button>
          )}
          <Button icon={<SaveOutlined />} onClick={handleSave} loading={saving} disabled={isLocked}>
            Save
          </Button>
          {!isNew && !isLocked && (
            <Button
              type="primary"
              icon={<SendOutlined />}
              onClick={handleSendCampaign}
              disabled={!campaign.stats?.totalRecipients}
              style={{ background: '#E31B23', borderColor: '#E31B23' }}
            >
              Send Campaign
            </Button>
          )}
        </Space>
      </TopBar>

      <TabsContainer>
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          items={[
            { key: 'content', label: <span><FormOutlined /> Content</span> },
            { key: 'recipients', label: <span><TeamOutlined /> Recipients ({campaign.stats?.totalRecipients || 0})</span>, disabled: isNew },
            { key: 'preview', label: <span><EyeOutlined /> Preview</span> }
          ]}
        />
      </TabsContainer>

      <ContentArea>
        <div className="builder-inner">
        {activeTab === 'content' && (
          <>
            <BrandingCard>
              <h3>Email Details</h3>
              <label style={{ display: 'block', marginBottom: 6 }}>From Name</label>
              <Input
                value={campaign.fromName}
                onChange={(e) => updateCampaign({ fromName: e.target.value })}
                style={{ marginBottom: 12 }}
                disabled={isLocked}
              />
              <label style={{ display: 'block', marginBottom: 6 }}>From Email</label>
              <Select
                value={campaign.fromEmail || undefined}
                onChange={(v) => {
                  const sender = verifiedSenders.find((s) => s.email === v);
                  updateCampaign({
                    fromEmail: v,
                    fromName: sender?.name || campaign.fromName
                  });
                }}
                style={{ width: '100%', marginBottom: 4 }}
                placeholder="Select a verified sender..."
                disabled={isLocked}
                options={verifiedSenders.map((s) => ({
                  value: s.email,
                  label: (
                    <Space size={6}>
                      <CheckCircleFilled style={{ color: '#52c41a' }} />
                      <span>{s.email}</span>
                      {s.name && s.name !== s.email && (
                        <span style={{ color: '#8c8c8c' }}>— {s.name}</span>
                      )}
                    </Space>
                  )
                }))}
                notFoundContent="No verified senders. Verify a sender in SendGrid first."
              />
              <p style={{ color: '#8c8c8c', fontSize: 12, margin: '0 0 12px' }}>
                Only addresses verified in SendGrid can be used as the From address.
              </p>
              <label style={{ display: 'block', marginBottom: 6 }}>Reply-To (optional)</label>
              <Input
                value={campaign.replyTo}
                onChange={(e) => updateCampaign({ replyTo: e.target.value })}
                style={{ marginBottom: 12 }}
                disabled={isLocked}
              />
              <label style={{ display: 'block', marginBottom: 6 }}>Subject</label>
              <Input
                value={campaign.subject}
                onChange={(e) => updateCampaign({ subject: e.target.value })}
                placeholder="Subject line — supports {{variables}}"
                disabled={isLocked}
              />
            </BrandingCard>

            <BrandingCard>
              <h3>Template & Survey</h3>
              <label style={{ display: 'block', marginBottom: 6 }}>Use Template (optional)</label>
              <Select
                value={campaign.templateId}
                onChange={applyTemplate}
                style={{ width: '100%', marginBottom: 12 }}
                placeholder="Select a template to start from..."
                allowClear
                disabled={isLocked}
                options={templates.map((t) => ({ value: t._id, label: t.name }))}
              />
              <label style={{ display: 'block', marginBottom: 6 }}>
                Link to Survey (optional)
                <Tooltip title="When linked, the survey URL is available in your email as {{surveyUrl}} and survey responses are attributed to this campaign.">
                  <span style={{ marginLeft: 4, color: '#8c8c8c' }}>(?)</span>
                </Tooltip>
              </label>
              <Select
                value={campaign.linkedSurveyId?._id || campaign.linkedSurveyId}
                onChange={(v) => updateCampaign({ linkedSurveyId: v })}
                style={{ width: '100%' }}
                placeholder="Optionally link to an active survey..."
                allowClear
                disabled={isLocked}
                options={surveys.map((s) => ({ value: s._id, label: s.title }))}
              />
            </BrandingCard>

            <BrandingCard>
              <EmailBodyEditor
                value={campaign.html}
                onChange={(html) => updateCampaign({ html })}
                disabled={isLocked}
              />
            </BrandingCard>

            <BrandingCard>
              <h3>Plain Text Version (optional)</h3>
              <p style={{ color: '#8c8c8c', marginTop: 0 }}>
                Auto-generated from HTML if left blank. Recommended for deliverability.
              </p>
              <TextArea
                rows={6}
                value={campaign.text}
                onChange={(e) => updateCampaign({ text: e.target.value })}
                disabled={isLocked}
              />
            </BrandingCard>
          </>
        )}

        {activeTab === 'recipients' && (
          <>
            <BrandingCard>
              <Space style={{ marginBottom: 16, justifyContent: 'space-between', width: '100%' }}>
                <h3 style={{ margin: 0 }}>Recipients</h3>
                {!isLocked && (
                  <Button type="primary" icon={<PlusOutlined />} onClick={() => setAddRecipientsModalOpen(true)}>
                    Add Recipients
                  </Button>
                )}
              </Space>

              {recipients.length === 0 ? (
                <Empty description="No recipients added yet" />
              ) : (
                <Table
                  columns={[
                    { title: 'Email', dataIndex: 'email' },
                    { title: 'Name', render: (_, r) => `${r.firstName || ''} ${r.lastName || ''}`.trim() || '—' },
                    {
                      title: 'Status',
                      dataIndex: 'status',
                      render: (s) => <Tag color={
                        s === 'pending' ? 'default'
                          : s === 'sent' ? 'blue'
                          : s === 'delivered' ? 'cyan'
                          : s === 'opened' ? 'gold'
                          : s === 'clicked' ? 'green'
                          : s === 'failed' || s === 'bounced' ? 'red'
                          : 'default'
                      }>{s}</Tag>
                    },
                    {
                      title: 'Opens',
                      dataIndex: 'openCount',
                      width: 80
                    },
                    {
                      title: 'Clicks',
                      dataIndex: 'clickCount',
                      width: 80
                    }
                  ]}
                  dataSource={recipients}
                  rowKey="_id"
                  size="small"
                  pagination={{
                    current: recipientPagination.current,
                    pageSize: recipientPagination.pageSize,
                    total: recipientPagination.total,
                    onChange: (page, pageSize) => setRecipientPagination((p) => ({ ...p, current: page, pageSize }))
                  }}
                />
              )}
            </BrandingCard>
          </>
        )}

        {activeTab === 'preview' && (
          <BrandingCard>
            <h3>Preview</h3>
            <p style={{ color: '#8c8c8c' }}>
              Subject: <strong>{campaign.subject}</strong><br />
              From: {campaign.fromName} &lt;{campaign.fromEmail}&gt;
            </p>
            <div
              style={{
                border: '1px solid #f0f0f0',
                padding: 24,
                borderRadius: 8,
                background: '#fff',
                marginTop: 12
              }}
              dangerouslySetInnerHTML={{
                __html: (campaign.html || '')
                  .replace(/\{\{firstName\}\}/g, 'Sample')
                  .replace(/\{\{lastName\}\}/g, 'User')
                  .replace(/\{\{email\}\}/g, 'sample@example.com')
                  .replace(/\{\{surveyUrl\}\}/g, '#preview-survey-link')
              }}
            />
          </BrandingCard>
        )}
        </div>
      </ContentArea>

      {/* Add Recipients Modal */}
      <Modal
        title="Add Recipients"
        open={addRecipientsModalOpen}
        onCancel={() => setAddRecipientsModalOpen(false)}
        onOk={handleAddRecipients}
        okText="Add"
        width={600}
      >
        <p style={{ color: '#8c8c8c' }}>
          One per line. Format: <code>email</code> or <code>email,firstName,lastName</code>
        </p>
        <TextArea
          rows={12}
          value={recipientsBulkText}
          onChange={(e) => setRecipientsBulkText(e.target.value)}
          placeholder={`alice@example.com,Alice,Smith\nbob@example.com,Bob,Jones\ncharlie@example.com`}
          style={{ fontFamily: 'monospace' }}
        />
      </Modal>

      {/* Test Email Modal */}
      <Modal
        title="Send Test Email"
        open={testEmailModalOpen}
        onCancel={() => setTestEmailModalOpen(false)}
        onOk={handleSendTest}
        okText="Send Test"
      >
        <p>Send a preview of this email to a single address.</p>
        <Input
          placeholder="test@example.com"
          value={testEmail}
          onChange={(e) => setTestEmail(e.target.value)}
        />
      </Modal>
    </BuilderContainer>
  );
};

export default CampaignBuilder;
